import { BayLevelEnum } from "open-vessel-definition";
import { IBayCgsInfo } from "./getContainersCgs";
import { IBayLevelAdjustedBottomBase } from "../types/IBayLevelAdjustedBottomBase";
import { IBayPattern } from "../types/IPositionPatterns";
import { VCG_SEPARATION_TO_DECK } from "../consts";
import { sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

/** Get the Most observed in between levels vcg */
export function calculateGeneralMedianVcg(
  bayLevelVcgsInfo: Record<IBayPattern, IBayCgsInfo>,
  missingImportantVcgs: boolean,
  adjustedBottomBases: Array<IBayLevelAdjustedBottomBase>,
): {
  generalMedianVcg: number;
  midVcgByBay: Record<IBayPattern, number>;
  adjustedBottomBasesDict: Record<string, number>;
} {
  const midVcgByBay: Record<IBayPattern, number> = {};
  const stats: Map<number, number> = new Map();

  const adjustedBottomBasesDict = missingImportantVcgs
    ? adjustedBottomBases.reduce((acc, bb) => {
        bb.bays.forEach((b) => {
          acc[`${b}-${bb.level}`] = bb.bottomBase;
        });
        return acc;
      }, {} as Record<string, number>)
    : ({} as Record<string, number>);

  (Object.keys(bayLevelVcgsInfo).sort(sortNumericAsc) as IBayPattern[]).forEach(
    (bay) => {
      const bayVcg = bayLevelVcgsInfo[bay];
      const bayVcgsAbove = bayVcg[BayLevelEnum.ABOVE];
      const bayVcgsBelow = bayVcg[BayLevelEnum.BELOW];

      if (bayVcgsAbove === undefined && bayVcgsBelow === undefined) return;

      const adjAboveBB =
        adjustedBottomBasesDict[`${bay}-${BayLevelEnum.ABOVE}`] || 0;
      const adjBelowBB =
        adjustedBottomBasesDict[`${bay}-${BayLevelEnum.BELOW}`] || 0;

      if (bayVcgsAbove === undefined && bayVcgsBelow !== undefined) {
        // If there is no aboveDeck, then we use the belowDeck height
        midVcgByBay[bay] =
          bayVcgsBelow.bottomBase +
          bayVcgsBelow.maxHeight +
          adjBelowBB +
          VCG_SEPARATION_TO_DECK;
      } else if (bayVcgsBelow === undefined && bayVcgsAbove !== undefined) {
        // If there is no belowDeck, then we use the aboveDeck bottomBase
        midVcgByBay[bay] =
          bayVcgsAbove.bottomBase + adjAboveBB - VCG_SEPARATION_TO_DECK;
      } else if (bayVcgsAbove !== undefined && bayVcgsBelow !== undefined) {
        const aboveBottomBase = bayVcgsAbove.bottomBase + adjAboveBB;
        const belowBottomBase =
          bayVcgsBelow.bottomBase + adjBelowBB + bayVcgsBelow.maxHeight;

        midVcgByBay[bay] = (aboveBottomBase + belowBottomBase) / 2;
      }

      stats.set(midVcgByBay[bay], (stats.get(midVcgByBay[bay]) || 0) + 1);
    },
  );

  const generalMedianVcg =
    Array.from(stats.keys()).sort((a, b) => b - a)[0] || 0;

  return {
    midVcgByBay,
    generalMedianVcg,
    adjustedBottomBasesDict,
  };
}
